import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ButtonIcon,
  Icon,
  Input,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';

import translations from 'src/lang';
import { getItemIcon, isEmpty } from 'src/utils';
import { getCurrentProductDataSources } from 'src/selectors/myCompany';
import {
  APPS_MAP,
  PRODUCTS_COLORS,
  PRODUCTS_ICONS,
} from '../../constants/config';
import {
  StyledTabsContainer,
  StyledTabs,
  StyledTabItem,
  StyledTabItemWrapper,
  StyledTabContent,
  StyledContent,
  StyledTabIcon,
  StyledAppTab,
} from './StyledTabs';

const getBorder = props => {
  if (props.noEditable) {
    return css`
      cursor: pointer;
    `;
  }
  return css`
    cursor: text;
    border-bottom: 2px dotted
      ${props => (props.selected ? 'white' : 'rgb(241,240,255)')};
    &:hover {
      border-bottom: 2px dotted
        ${props => props.color && props.theme.colors[props.color]};
    }
  `;
};

const StyledText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  max-width: 100%;
  ${props => getBorder(props)}
`;

const ApplicationTabs = ({
  children,
  selectedTab,
  onTabChange,
  onTabClose,
  theme,
  timeframes,
  taxonomyList,
  requestSaveAudience,
  requestRenameItem,
  ...rest
}) => {
  const productDataSources = useSelector(state =>
    getCurrentProductDataSources(state)
  );
  const [inEdit, setInEdit] = useState([]);
  const getTabLabel = useCallback(tab => {
    let prefix = translations['en-US'].COMMON.ANALYZE;
    if (tab.app === APPS_MAP.SEGMENTATION) {
      prefix = translations['en-US'].COMMON.SEGMENTATION;
    }
    if (tab.app === APPS_MAP.COMPARE) {
      return translations['en-US'].COMMON.COMPARE;
    }
    return `${tab.audience.label} - ${prefix}`;
  }, []);

  const onSave = useCallback(
    (tab, newLabel) => {
      if (tab.audience.persistence === 'permanent') {
        requestRenameItem(tab.audience.id, newLabel);
      } else {
        requestSaveAudience(tab.audience.id, {
          label: newLabel,
          parent_id: 0,
        });
      }
    },
    [requestSaveAudience, requestRenameItem]
  );

  const getTabContent = useCallback(
    tab => {
      const selected = selectedTab === tab.id;
      const inEditItemIndex = inEdit.findIndex(e => e.id === tab.id);
      const inEditItem = inEdit[inEditItemIndex];
      const onSubmit = () => {
        onSave(tab, inEditItem.value);
        setInEdit(prev => {
          return prev.filter((_, i) => i !== inEditItemIndex);
        });
      };

      if (tab.app === APPS_MAP.COMPARE) {
        return (
          <StyledText
            size="small"
            align="left"
            title=""
            color={tab.loaded ? PRODUCTS_COLORS[tab.app] : 'light_darker_1'}
            noEditable
            selected={selected}
          >
            {getTabLabel(tab)}
          </StyledText>
        );
      }
      return (
        <>
          {inEditItem ? (
            <form onSubmit={onSubmit}>
              <Input
                type="text"
                name="tab-edit"
                size="thin"
                value={inEditItem.value}
                onChange={e => {
                  e.persist();
                  setInEdit(prev => {
                    const next = [...prev];
                    next[inEditItemIndex] = {
                      id: tab.id,
                      value: e.target.value,
                    };
                    return next;
                  });
                }}
                onBlur={onSubmit}
                autoFocus
              />
            </form>
          ) : (
            <Tooltip
              position="bottom"
              content={tab.audience.label}
              customTooltipStyle={{
                overflowWrap: 'anywhere',
              }}
              tooltipStyle={{ maxWidth: '90%' }}
            >
              <StyledText
                size="small"
                align="left"
                title={tab.audience.label}
                color={tab.loaded ? PRODUCTS_COLORS[tab.app] : 'light_darker_1'}
                selected={selected}
                onDoubleClick={e => {
                  e.persist();
                  setInEdit(prev =>
                    prev.concat({
                      value: tab.audience.label,
                      id: tab.id,
                    })
                  );
                }}
              >
                {getTabLabel(tab)}
              </StyledText>
            </Tooltip>
          )}
        </>
      );
    },
    [getTabLabel, inEdit, onSave, selectedTab]
  );

  return (
    <StyledTabsContainer>
      <StyledTabs role="tablist" {...rest}>
        {React.Children.map(children, ({ props: { tab } }) => {
          const selected = selectedTab === tab.id;

          return (
            <StyledTabItem
              role="tab"
              selected={selected}
              aria-selected={selected ? 'true' : 'false'}
              disabled={tab.disabled}
              closable={typeof onTabClose === 'function'}
              onClick={() => onTabChange && onTabChange(tab.id)}
              appContainer={tab.app}
            >
              <StyledTabItemWrapper>
                <StyledTabContent>
                  <StyledTabIcon loaded={tab.loaded}>
                    <Icon
                      size="small"
                      icon={
                        !isEmpty(productDataSources) && tab.app === 'analyze'
                          ? getItemIcon(tab.audience, productDataSources)
                          : PRODUCTS_ICONS[tab.app]
                      }
                      variant={
                        tab.loaded ? PRODUCTS_COLORS[tab.app] : 'light_darker_1'
                      }
                    />
                  </StyledTabIcon>
                  {getTabContent(tab)}
                </StyledTabContent>
                <ButtonIcon
                  size={'xsmall'}
                  disabled={tab.disabled}
                  icon="remove"
                  variant="dark_lighter"
                  onClick={e => {
                    e.stopPropagation();
                    onTabClose && onTabClose(tab.id);
                  }}
                />
              </StyledTabItemWrapper>
            </StyledTabItem>
          );
        })}
      </StyledTabs>

      <StyledContent>
        {React.Children.map(children, comp => {
          const {
            props: { tab },
          } = comp;

          return (
            <StyledAppTab
              id={`hx-ws-tab_${tab.id}`}
              selected={selectedTab === tab.id}
            >
              {comp}
            </StyledAppTab>
          );
        })}
      </StyledContent>
    </StyledTabsContainer>
  );
};

ApplicationTabs.displayName = 'ApplicationTabs';

ApplicationTabs.propTypes = {
  children: PropTypes.any,
  selectedTab: PropTypes.any,
  onTabChange: PropTypes.func,
};

export default ApplicationTabs;
